import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';
import { supabase, retryOperation } from '../lib/supabase';

export default function Subscribe() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Create unauth user first
      const { data: userId, error: userError } = await retryOperation(() =>
        supabase.rpc('handle_unauth_user', {
          user_email: email.toLowerCase()
        })
      );

      if (userError) throw userError;

      // Submit to Netlify Forms
      const formData = new FormData();
      formData.append('form-name', 'newsletter');
      formData.append('email', email);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Store email for future use
      localStorage.setItem('blooma_email', email);
      
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1EEE7] flex flex-col">
      {/* Hero Section */}
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
          <div className="flex justify-center mb-6">
            <Logo size="lg" showText={false} variant="full" />
          </div>
          
          <h1 className="text-2xl font-bold text-center mb-2">Join Our Gardening Community</h1>
          <p className="text-gray-600 text-center mb-8">
            Get personalized gardening tips, seasonal reminders, and exclusive content delivered to your inbox.
          </p>

          {success ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Thank You!</h2>
              <p className="text-gray-600 mb-6">
                You've been added to our mailing list. Look out for gardening tips and updates coming your way soon!
              </p>
              <a 
                href="/"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <span>Return to Homepage</span>
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          ) : (
            <form 
              onSubmit={handleSubmit}
              name="newsletter"
              method="POST"
              data-netlify="true"
              className="space-y-4"
            >
              {/* Hidden input for Netlify Forms */}
              <input type="hidden" name="form-name" value="newsletter" />
              
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  required
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <span>Subscribe Now</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                By subscribing, you agree to receive occasional updates about new gardening resources.
                We respect your privacy and will never share your information.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white py-6 border-t">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Blooma Grows. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}